import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useArchiveMentionToDrive } from '@/hooks/actions/use-archive-mention-to-drive';
import { Box, Button, Link, VStack, Text } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ArchiveConfirmDialogProps {
  isOpen: boolean;
  mentionId: string;
  isCreatorEnrolled: boolean;
  onClose: () => void;
  onConfirmed: (result: { archiveId: string; status: string; isDuplicate: boolean }) => void;
}

/** Minimum time the modal must be visible before the user can confirm. */
const MIN_CONFIRMATION_DELAY_MS = 1500;

/**
 * GDPR-aware confirmation modal for the Save-to-Drive flow.
 *
 * - Three independent disclaimers must be acknowledged.
 * - The CTA stays disabled for 1.5s after open to ensure the user reads them.
 * - For non-affiliate creators, an additional warning banner is displayed and
 *   passed to the backend via nonAffiliateDisclaimerShown=true.
 * - On error, we keep the dialog open so the user can retry; the mutation
 *   surfaces the toast.
 */
export const ArchiveConfirmDialog: React.FC<ArchiveConfirmDialogProps> = ({
  isOpen,
  mentionId,
  isCreatorEnrolled,
  onClose,
  onConfirmed,
}) => {
  const { t, i18n } = useTranslation();
  const archiveMutation = useArchiveMentionToDrive();

  // Locale-scoped public privacy page on the marketing site.
  const policyLocale = i18n.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const policyUrl = `https://www.fluenceur.com/${policyLocale}/creator-privacy`;

  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [check3, setCheck3] = useState(false);
  const [delayElapsed, setDelayElapsed] = useState(false);

  // Reset state every time the dialog (re)opens.
  useEffect(() => {
    if (!isOpen) {
      setCheck1(false);
      setCheck2(false);
      setCheck3(false);
      setDelayElapsed(false);
      return;
    }

    setDelayElapsed(false);
    const timer = setTimeout(() => {
      setDelayElapsed(true);
    }, MIN_CONFIRMATION_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const allChecked = check1 && check2 && check3;
  const isPending = archiveMutation.isPending;
  const canConfirm = allChecked && delayElapsed && !isPending;

  const handleOpenChange = ({ open }: { open: boolean }) => {
    if (!open && !isPending) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    try {
      const response = await archiveMutation.mutateAsync({
        mentionId,
        disclaimerAcknowledged: true,
        nonAffiliateDisclaimerShown: !isCreatorEnrolled || undefined,
      });

      if (response.data) {
        onConfirmed({
          archiveId: response.data.archiveId,
          status: response.data.status,
          isDuplicate: response.data.isDuplicate,
        });
      }
    } catch {
      // Error toast is shown by the mutation hook; keep modal open so the user can retry.
    }
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="center"
      closeOnInteractOutside={false}
      closeOnEscape={!isPending}
    >
      <DialogContent rounded="2xl" maxW="md">
        <DialogHeader>
          <DialogTitle>{t('mentions.archive.modal.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text fontSize="sm" color="gray.700">
              {t('mentions.archive.modal.subtitle')}
            </Text>

            {!isCreatorEnrolled && (
              <Alert
                status="warning"
                variant="subtle"
                borderRadius="md"
                data-testid="non-affiliate-banner"
              >
                {t('mentions.archive.modal.non_affiliate_banner')}
              </Alert>
            )}

            <VStack align="stretch" gap={2.5}>
              <Checkbox
                checked={check1}
                onCheckedChange={(e) => setCheck1(Boolean(e.checked))}
                alignItems="flex-start"
                data-testid="archive-checkbox-1"
              >
                <Box fontSize="sm" lineHeight="snug">
                  {t('mentions.archive.modal.checkbox_1')}
                </Box>
              </Checkbox>
              <Checkbox
                checked={check2}
                onCheckedChange={(e) => setCheck2(Boolean(e.checked))}
                alignItems="flex-start"
                data-testid="archive-checkbox-2"
              >
                <Box fontSize="sm" lineHeight="snug">
                  {t('mentions.archive.modal.checkbox_2')}
                </Box>
              </Checkbox>
              <Checkbox
                checked={check3}
                onCheckedChange={(e) => setCheck3(Boolean(e.checked))}
                alignItems="flex-start"
                data-testid="archive-checkbox-3"
              >
                <Box fontSize="sm" lineHeight="snug">
                  {t('mentions.archive.modal.checkbox_3')}
                </Box>
              </Checkbox>
            </VStack>

            <Link
              href={policyUrl}
              target="_blank"
              rel="noopener noreferrer"
              fontSize="xs"
              color="gray.500"
            >
              {t('mentions.archive.modal.policy_link')}
            </Link>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              {t('mentions.archive.modal.cta_cancel')}
            </Button>
          </DialogActionTrigger>
          <Button
            colorPalette="gray"
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={isPending}
            data-testid="archive-confirm-cta"
          >
            {t('mentions.archive.modal.cta_confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
